import { initSlice } from 'aa_common/front-end/zustand'
import { AxiosResponse } from 'axios'

import { tryCallApi } from '../helpers'
import { FeatureFlagsService, PresenterEvaluateBatchFeatureFlagResponse } from '../open-api'

export enum FeatureFlagNames {
  FixedAssetReduction = 'fixed_asset_reduction',
  RefactorPagePhase8 = 'refactor_page_phase8',
  RefactorPagePhase10 = 'refactor_page_phase10',
  RefactorPagePhase11 = 'refactor_page_phase11',
  RefactorPagePhase12 = 'refactor_page_phase12',
  DisableReductionDividing = 'disable_reduction_dividing',
  RegisterAcceleratedDepreciation = 'register-accelerated-depr',
  DepartmentDepreciationAmountReport = 'department_depreciation_amount',
  ReductionIncreaseDecreaseReport = 'apply-feature-reduction-increase-decrease-report',
  ImprovePerformanceReportPages = 'improve_performance_report_pages',
}

export type FeatureNameType =
  | FeatureFlagNames.FixedAssetReduction
  | FeatureFlagNames.RefactorPagePhase8
  | FeatureFlagNames.RefactorPagePhase10
  | FeatureFlagNames.RefactorPagePhase11
  | FeatureFlagNames.RefactorPagePhase12
  | FeatureFlagNames.DisableReductionDividing
  | FeatureFlagNames.RegisterAcceleratedDepreciation
  | FeatureFlagNames.DepartmentDepreciationAmountReport
  | FeatureFlagNames.ReductionIncreaseDecreaseReport
  | FeatureFlagNames.ImprovePerformanceReportPages

const initState: Partial<Record<FeatureNameType, boolean>> = {}

export const openFeatureSlice = initSlice('open-features', initState)

export const featureList: FeatureType[] = [
  {
    featureName: FeatureFlagNames.FixedAssetReduction,
    defaultValue: false,
    onProduction: true,
  },
  {
    featureName: FeatureFlagNames.RefactorPagePhase8,
    defaultValue: false,
    onProduction: true,
  },
  {
    featureName: FeatureFlagNames.RefactorPagePhase10,
    defaultValue: false,
    onProduction: true,
  },
  {
    featureName: FeatureFlagNames.RefactorPagePhase11,
    defaultValue: false,
    onProduction: false,
  },
  {
    featureName: FeatureFlagNames.RefactorPagePhase12,
    defaultValue: false,
    onProduction: false,
  },
  {
    featureName: FeatureFlagNames.RegisterAcceleratedDepreciation,
    defaultValue: false,
  },
  {
    featureName: FeatureFlagNames.DisableReductionDividing,
    defaultValue: false,
    onProduction: true,
  },
  {
    featureName: FeatureFlagNames.DepartmentDepreciationAmountReport,
    defaultValue: false,
    onProduction: true,
  },
  {
    featureName: FeatureFlagNames.ReductionIncreaseDecreaseReport,
    defaultValue: false,
    onProduction: true,
  },
  {
    featureName: FeatureFlagNames.ImprovePerformanceReportPages,
    defaultValue: false,
    onProduction: true,
  },
]

export const isOpenFeatureDevMode = () => {
  const { REACT_APP_ENV, REACT_APP_RELAY_URL } = process.env
  return REACT_APP_ENV === 'local' && !REACT_APP_RELAY_URL
}

const isTestEnv = () => ['staging', 'beta', 'idev'].includes(String(process.env.REACT_APP_ENV))

export const checkAllFeatures = async (isPublic?: boolean) => {
  const state: Record<string | number, boolean> = {}

  const featureFlags = featureList.map(({ featureName, defaultValue, onProduction }) => ({
    key: featureName,
    default_value: isTestEnv() ? onProduction || defaultValue : defaultValue,
  }))

  let response: {
    data?: AxiosResponse<PresenterEvaluateBatchFeatureFlagResponse>
    error?: any
  }
  if (!isPublic) {
    response = await tryCallApi(FeatureFlagsService.evaluateBatchCreate)({ bool_flags: featureFlags })
  } else {
    response = await tryCallApi(FeatureFlagsService.featureFlagsEvaluateBatchCreate)({ bool_flags: featureFlags })
  }

  response?.data?.data?.bool_flags?.forEach(({ key, enabled }) => {
    if (key) {
      state[key] = enabled ?? false

      if (isOpenFeatureDevMode()) {
        state[key] = true
      }
    }
  })

  isTestEnv() &&
    featureList.forEach(({ featureName, onProduction, defaultValue }) => {
      state[featureName] = state[featureName] ?? (onProduction || defaultValue)
    })

  openFeatureSlice.setState(state, 'set-open-features')
}

type FeatureType = {
  featureName: FeatureNameType
  defaultValue: any
  onProduction?: boolean
}
